/**
 * Alignement avec le backend Django : stock illimité si stock_tracking=false (produit)
 * ou stock_management=false (variation). Dans ces cas le champ quantité/stock peut être 0.
 */

const UNLIMITED = Number.MAX_SAFE_INTEGER;

type VariationLike = {
  quantite?: number;
  stock_management?: boolean | string;
  id?: string;
};

function isUnlimitedFlag(val: unknown): boolean {
  return val === false || val === "false" || val === 0;
}

function pickVariationForCart(variations: VariationLike[]): VariationLike | undefined {
  if (!variations.length) return undefined;
  return (
    variations.find((v) => Number(v?.quantite ?? 0) > 0) ||
    variations.find((v) => isUnlimitedFlag(v?.stock_management)) ||
    variations[0]
  );
}

/** Produit achetable (affichage boutons panier / CTA) */
export function isProductAvailableForPurchase(item: {
  stock?: string | number;
  stock_tracking?: boolean | string;
  variations?: VariationLike[];
} | null | undefined): boolean {
  if (!item) return false;
  if (isUnlimitedFlag(item.stock_tracking)) return true;

  const variations = Array.isArray(item.variations) ? item.variations : [];
  if (variations.length > 0) {
    return variations.some(
      (v) =>
        isUnlimitedFlag(v?.stock_management) || Number(v?.quantite ?? 0) > 0
    );
  }

  const n = Number(item.stock);
  return Number.isFinite(n) && n > 0;
}

/**
 * Stock numérique pour le slice panier (incrément : quantite < stock).
 * Stock illimité → MAX_SAFE_INTEGER.
 */
export function effectiveStockForCartItem(item: {
  stock?: string | number;
  stock_tracking?: boolean | string;
  variations?: VariationLike[];
  type?: string;
} | null | undefined): number {
  if (!item) return 0;
  if (isUnlimitedFlag(item.stock_tracking)) return UNLIMITED;

  const variations = Array.isArray(item.variations) ? item.variations : [];
  if (variations.length > 0) {
    const picked = pickVariationForCart(variations);
    if (picked && isUnlimitedFlag(picked.stock_management)) return UNLIMITED;
    const q = Number(picked?.quantite ?? 0);
    return Number.isFinite(q) ? q : 0;
  }

  const n = Number(item.stock);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Stock pour le panier sur la fiche détail (respecte la variation sélectionnée).
 */
export function effectiveStockForCartSelection(
  item: {
    stock?: string | number;
    stock_tracking?: boolean | string;
    type?: string;
  } | null | undefined,
  variationActive: VariationLike | null | undefined
): number {
  if (!item) return 0;
  if (item.stock_tracking === false || item.stock_tracking === "false") {
    return UNLIMITED;
  }

  if (item.type === "taille_unique") {
    const n = Number(item.stock);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  if (!variationActive) return 0;
  if (variationIsUnlimited(variationActive)) return UNLIMITED;
  const q = Number(variationActive.quantite ?? 0);
  return Number.isFinite(q) ? q : 0;
}

export function variationsForCartPayload(item: {
  variations?: VariationLike[];
}): VariationLike[] {
  const variationsList = Array.isArray(item?.variations) ? item.variations : [];
  if (variationsList.length === 0) return [];
  const picked = pickVariationForCart(variationsList);
  return picked ? [picked] : [];
}

/** Au moins une unité ou variation à stock illimité */
export function variationHasAvailableStock(
  v: VariationLike | null | undefined
): boolean {
  if (!v) return false;
  if (v.stock_management === false || v.stock_management === "false") return true;
  return Number(v.quantite ?? 0) > 0;
}

function variationIsUnlimited(v: VariationLike | null | undefined): boolean {
  if (!v) return false;
  return v.stock_management === false || v.stock_management === "false";
}

/** Export pour conditions UI (rupture, etc.) */
export function isVariationStockUnlimited(
  v: VariationLike | null | undefined
): boolean {
  return variationIsUnlimited(v);
}

/**
 * Quantité max sélectionnable sur la fiche produit (affiche + / clamp).
 * — taille_unique : stock produit
 * — autres types : stock de la variation active (couleur / taille)
 */
export function getMaxSelectableQuantity(
  item: {
    stock?: string | number;
    stock_tracking?: boolean | string;
    variations?: VariationLike[];
    type?: string;
  } | null | undefined,
  variationActive: VariationLike | null | undefined
): number {
  if (!item) return 0;

  if (item.stock_tracking === false || item.stock_tracking === "false") {
    return UNLIMITED;
  }

  if (item.type === "taille_unique") {
    const n = Number(item.stock);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  if (!variationActive) return 0;
  if (variationIsUnlimited(variationActive)) return UNLIMITED;
  const q = Number(variationActive.quantite ?? 0);
  return Number.isFinite(q) ? q : 0;
}

/**
 * Active les boutons « Acheter maintenant » / « Ajouter au panier » sur la fiche détail.
 */
export function detailPageBuyButtonsEnabled(
  item: {
    stock?: string | number;
    stock_tracking?: boolean | string;
    variations?: VariationLike[];
    type?: string;
  } | null | undefined,
  count: number,
  variationActive: VariationLike | null | undefined,
  validation: boolean
): boolean {
  if (!item || count <= 0) return false;
  if (!isProductAvailableForPurchase(item)) return false;

  if (item.type === "taille_unique") {
    return !!validation;
  }

  if (!variationActive || !validation) return false;
  if (variationIsUnlimited(variationActive)) return true;

  const qty = Number(variationActive.quantite ?? 0);
  return qty > 0 && count <= qty;
}
